import React, { useMemo, useState } from 'react';
import { Modal, Button, Typography, Stack } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useRecoilState } from 'recoil';
import './CustomDatePickerModal.scss';
import {
  dataTrendPeriodAtom,
  selectedDateAtom,
  DataTrendPeriod,
  getWeekDatesOf,
} from '../../../../../data-access';
import { ToggleButtons } from '../../../../common';
import { useTranslation } from 'react-i18next';
import { formatDataDate, getWeekdayLabels } from '../../../i18n';

interface CustomDatePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Dayjs;
}

const isSameDay = (date1: Dayjs | null, date2: Dayjs | null) =>
  date1 && date2 && dayjs(date1).isSame(dayjs(date2), 'day');

const isBetween = (date: Dayjs, start: Dayjs | null, end: Dayjs | null) => {
  if (!start || !end) return false;
  return dayjs(date).isAfter(start, 'day') && dayjs(date).isBefore(end, 'day');
};

const getWeekRange = (date: Dayjs) => {
  const weekDates = getWeekDatesOf(date);
  return { startOfWeek: weekDates[0], endOfWeek: weekDates[6] };
};

const generateDaysInMonth = (month: Dayjs = dayjs()) => {
  const startOfMonth = month.startOf('month');
  const endOfMonth = month.endOf('month');
  const days: (Dayjs | null)[] = [];

  const startWeekDay = startOfMonth.day();
  const totalDays = endOfMonth.date();

  for (let i = 0; i < startWeekDay; i++) {
    days.push(null);
  }

  for (let i = 1; i <= totalDays; i++) {
    days.push(dayjs(month).date(i));
  }

  return days;
};

export const CustomDatePickerModal: React.FC<CustomDatePickerModalProps> = ({
  isOpen,
  onClose,
  selectedDate,
}) => {
  const { t, i18n } = useTranslation();
  const [selectedDateAtomValue, setSelectedDateAtomValue] = useRecoilState(selectedDateAtom);
  const [dataTrendPeriod, setDataTrendPeriod] = useRecoilState(dataTrendPeriodAtom);

  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const periodOptions = useMemo(
    () => [
      { value: 'daily' as DataTrendPeriod, label: t('data.common.daily') },
      { value: 'weekly' as DataTrendPeriod, label: t('data.common.weekly') },
    ],
    [t]
  );
  const weekdayLabels = useMemo(() => getWeekdayLabels(t, 'short'), [t]);

  const handleTrendPeriodChange = (value: DataTrendPeriod) => {
    setDataTrendPeriod(value);
  };

  const handleDateClick = (date: Dayjs) => {
    setSelectedDateAtomValue(date);
  };

  const handleSave = () => {
    onClose();
  };

  const goToPrevMonth = () => setCurrentMonth(currentMonth.subtract(1, 'month'));
  const goToNextMonth = () => setCurrentMonth(currentMonth.add(1, 'month'));

  const days = generateDaysInMonth(currentMonth);

  const weekRange =
    selectedDateAtomValue && dataTrendPeriod === 'weekly'
      ? getWeekRange(selectedDateAtomValue)
      : null;

  return (
    <Modal open={isOpen} onClose={onClose} className="custom-date-picker-modal">
      <div className="custom-date-picker-modal__content">
        <Typography variant="h6">{t('data.customDate.selectCustomDate')}</Typography>

        <div className="custom-date-picker-modal__period-selector">
          <ToggleButtons
            value={dataTrendPeriod}
            options={periodOptions}
            onChange={handleTrendPeriodChange}
            className="custom-date-picker-modal__toggle-group"
          />
        </div>

        <div className="custom-date-picker-modal__date-picker">
          <div className="custom-date-picker-modal__header">
            <button onClick={goToPrevMonth} className="custom-date-picker-modal__header-button">
              ◀
            </button>
            <div>
              {formatDataDate(i18n.language, currentMonth.toDate(), {
                month: 'long',
                year: 'numeric',
              })}
            </div>
            <button onClick={goToNextMonth} className="custom-date-picker-modal__header-button">
              ▶
            </button>
          </div>

          <div className="custom-date-picker-modal__weekdays">
            {weekdayLabels.map((d) => (
              <div key={d} className="custom-date-picker-modal__weekday">
                {d}
              </div>
            ))}
          </div>

          <div className="custom-date-picker-modal__days-grid">
            {days.map((date, idx) => {
              const isSelectedDate = isSameDay(date, selectedDateAtomValue);
              const isToday = isSameDay(date, dayjs());
              const isInWeekRange =
                date && weekRange && dataTrendPeriod === 'weekly'
                  ? isSameDay(date, weekRange.startOfWeek) ||
                    isSameDay(date, weekRange.endOfWeek) ||
                    isBetween(date, weekRange.startOfWeek, weekRange.endOfWeek)
                  : false;

              return (
                <div
                  key={idx}
                  className={`custom-date-picker-modal__day-cell ${!date ? 'custom-date-picker-modal__day-cell--empty' : ''} 
                    ${isSelectedDate ? 'custom-date-picker-modal__day-cell--selected' : ''} 
                    ${isInWeekRange ? 'custom-date-picker-modal__day-cell--in-week' : ''}
                    ${isToday ? 'custom-date-picker-modal__day-cell--today' : ''}`}
                  onClick={() => date && handleDateClick(date)}
                >
                  {date ? date.date() : ''}
                </div>
              );
            })}
          </div>
        </div>

        <div className="custom-date-picker-modal__selected-info">
          <Typography variant="body2">
            <strong>{t('data.customDate.selected')}:</strong>{' '}
            {selectedDateAtomValue
              ? dataTrendPeriod === 'weekly' && weekRange
                ? `${formatDataDate(i18n.language, weekRange.startOfWeek.toDate(), {
                    month: 'short',
                    day: '2-digit',
                  })} - ${formatDataDate(i18n.language, weekRange.endOfWeek.toDate(), {
                    month: 'short',
                    day: '2-digit',
                    year: 'numeric',
                  })} (${t('data.common.week')})`
                : formatDataDate(i18n.language, selectedDateAtomValue.toDate(), {
                    month: 'short',
                    day: '2-digit',
                    year: 'numeric',
                  })
              : t('data.common.none')}
          </Typography>
        </div>

        <Stack direction="row" spacing={2} mt={2} justifyContent="flex-end">
          <Button
            variant="text"
            onClick={onClose}
            className="custom-date-picker-modal__cancel-button"
          >
            <Typography variant="body2">{t('data.common.close')}</Typography>
          </Button>
        </Stack>
      </div>
    </Modal>
  );
};
